import React from "react";
import { LegalLayout, H2, P, UL } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testid="legal-privacy"
      eyebrow="✦ HERITAGE BLOOM CLUB ✦"
      title="Privacy Policy"
    >
      <H2 n="1">Introduction</H2>
      <P>
        The Heritage Bloom Club is committed to protecting the privacy and personal information
        of our customers, growers, website visitors and community members.
      </P>
      <P>
        This Privacy Policy explains how we collect, use, store and protect personal information
        when you interact with the Heritage Bloom Club website, subscription platform, Growers
        Portal and related services.
      </P>
      <P>
        By using our services, you agree to the collection and use of information in accordance
        with this Privacy Policy.
      </P>

      <H2 n="2">Information We Collect</H2>
      <P>We may collect personal information including:</P>
      <P><strong>Customer Information</strong></P>
      <UL>
        <li>Name</li>
        <li>Email address</li>
        <li>Phone number</li>
        <li>Delivery address</li>
        <li>Billing address</li>
        <li>Subscription preferences</li>
        <li>Purchase history</li>
        <li>Delivery instructions</li>
      </UL>
      <P><strong>Grower Information</strong></P>
      <UL>
        <li>Name</li>
        <li>Business name</li>
        <li>Farm name</li>
        <li>Contact details</li>
        <li>Service areas</li>
        <li>Banking information for payments</li>
        <li>Business information supplied during application and onboarding</li>
      </UL>
      <P><strong>Website Information</strong></P>
      <P>When you visit our website, we may collect:</P>
      <UL>
        <li>IP address</li>
        <li>Browser information</li>
        <li>Device information</li>
        <li>Website usage data</li>
        <li>Cookies and analytics data</li>
      </UL>

      <H2 n="3">How We Use Your Information</H2>
      <P>We use personal information to:</P>
      <UL>
        <li>Process subscriptions and payments</li>
        <li>Coordinate deliveries</li>
        <li>Manage customer accounts</li>
        <li>Provide customer support</li>
        <li>Process grower applications</li>
        <li>Administer the Growers Portal</li>
        <li>Improve our services</li>
        <li>Send service-related communications</li>
        <li>Send marketing communications where consent has been provided</li>
        <li>Meet legal and regulatory obligations</li>
      </UL>
      <P>
        We only collect information reasonably necessary to operate the Heritage Bloom Club and
        provide our services.
      </P>

      <H2 n="4">Information Shared With Growers</H2>
      <P>To fulfil Bloom Club subscriptions, participating growers may receive limited customer
        information including:</P>
      <UL>
        <li>Customer name</li>
        <li>Delivery address</li>
        <li>Contact details where required</li>
        <li>Delivery instructions</li>
      </UL>
      <P>Growers may only use this information for purposes directly related to Bloom Club subscription fulfilment.</P>
      <P>Growers are prohibited from selling, sharing or using customer information for unauthorised purposes.</P>

      <H2 n="5">Marketing Communications</H2>
      <P>From time to time, we may send:</P>
      <UL>
        <li>Subscription updates</li>
        <li>Delivery notifications</li>
        <li>Seasonal announcements</li>
        <li>Promotional offers</li>
        <li>Grower stories</li>
        <li>Bloom Club news</li>
      </UL>
      <P>
        Customers may unsubscribe from marketing communications at any time by following the
        unsubscribe instructions provided in those communications.
      </P>
      <P>Operational communications relating to active subscriptions may still be sent where necessary.</P>

      <H2 n="6">Payment Information</H2>
      <P>Payments are processed through approved third-party payment providers.</P>
      <P>The Heritage Bloom Club does not store full credit card information on its own servers.</P>
      <P>Payment information is handled in accordance with the security standards of the relevant payment provider.</P>

      <H2 n="7">Cookies &amp; Analytics</H2>
      <P>Our website may use cookies and similar technologies to:</P>
      <UL>
        <li>Improve website functionality</li>
        <li>Analyse website traffic</li>
        <li>Enhance user experience</li>
        <li>Support marketing and advertising activities</li>
      </UL>
      <P>
        Users may disable cookies through their browser settings, although certain website
        features may not function correctly as a result.
      </P>

      <H2 n="8">Data Security</H2>
      <P>
        The Heritage Bloom Club takes reasonable administrative, technical and operational
        measures to protect personal information from unauthorised access, misuse, loss,
        alteration or disclosure.
      </P>
      <P>
        While no online system can guarantee absolute security, we are committed to maintaining
        appropriate safeguards to protect personal information.
      </P>

      <H2 n="9">Data Retention</H2>
      <P>We retain personal information only for as long as reasonably necessary to:</P>
      <UL>
        <li>Provide services</li>
        <li>Maintain business records</li>
        <li>Meet legal obligations</li>
        <li>Resolve disputes</li>
        <li>Enforce agreements</li>
      </UL>
      <P>Information that is no longer required may be securely deleted or anonymised.</P>

      <H2 n="10">Access &amp; Correction</H2>
      <P>You may request access to personal information we hold about you.</P>
      <P>You may also request correction of inaccurate or incomplete information.</P>
      <P>Requests can be submitted using the contact details provided below.</P>

      <H2 n="11">Third-Party Services</H2>
      <P>The Heritage Bloom Club may use trusted third-party providers for:</P>
      <UL>
        <li>Payment processing</li>
        <li>Email communications</li>
        <li>Website hosting</li>
        <li>Analytics</li>
        <li>Software infrastructure</li>
      </UL>
      <P>These providers may receive limited information necessary to perform their services on our behalf.</P>

      <H2 n="12">Children's Privacy</H2>
      <P>
        The Heritage Bloom Club does not knowingly collect personal information from children
        under the age of 13 without appropriate parental or guardian consent.
      </P>

      <H2 n="13">Changes To This Privacy Policy</H2>
      <P>
        We may update this Privacy Policy from time to time to reflect changes in our business,
        technology, legal obligations or services.
      </P>
      <P>Updated versions will be published on our website with the revised effective date.</P>

      <H2 n="14">Contact Us</H2>
      <P>
        If you have questions regarding this Privacy Policy or wish to request access to your
        personal information, please contact:
      </P>
      <UL>
        <li>Heritage Dahlias NZ Limited</li>
        <li>Email: <a href="mailto:heritagedahliasnz@gmail.com" className="text-[var(--berry-deep)] underline">heritagedahliasnz@gmail.com</a></li>
        <li>Website: <a href="https://heritagedahlias.nz" className="text-[var(--berry-deep)] underline">heritagedahlias.nz</a></li>
      </UL>

      <H2 n="15">Our Commitment</H2>
      <P>The Heritage Bloom Club was built on trust, community and supporting local growers.</P>
      <P>Protecting the privacy of our customers and growers is an important part of that commitment.</P>
      <P>
        We believe personal information should be treated with the same care and respect that we
        bring to every Bloom Box delivered throughout our communities.
      </P>

      <H2 n="16">Subscriber Protection</H2>
      <P>The Heritage Bloom Club guarantees the following to every subscriber and waitlist registrant:</P>
      <UL>
        <li>Your personal information will never be sold, leased, traded or licensed to any third party</li>
        <li>Your details are shared only with your allocated grower for the sole purpose of fulfilling your subscription</li>
        <li>Waitlist registrations do not create any payment obligation and may be removed on written request</li>
        <li>You may request access to, correction of, or deletion of your personal information at any time</li>
        <li>You will receive plain-language confirmation of every subscription, change, cancellation or refund</li>
        <li>You retain all rights available under the Privacy Act 2020, the Consumer Guarantees Act 1993 and the Fair Trading Act 1986</li>
        <li>Heritage will respond to any privacy or data-protection complaint in writing within seven (7) business days</li>
      </UL>

      <H2 n="17">Grower Protection</H2>
      <P>The Heritage Bloom Club protects the privacy and commercial information of its growers by:</P>
      <UL>
        <li>Treating grower banking, tax and personal details as strictly confidential</li>
        <li>Sharing grower contact information with subscribers only to the extent reasonably necessary to deliver Bloom Boxes</li>
        <li>Prohibiting any Heritage staff or contractor from approaching an active grower's allocated subscribers for competing services</li>
        <li>Restricting public disclosure of grower business performance, allocations and earnings</li>
        <li>Providing growers with access to the data Heritage holds about them on written request</li>
      </UL>

      <H2 n="18">Intellectual Property Protection</H2>
      <P>
        All Heritage Bloom Club brand assets, logos, photography, illustrations, written copy,
        the seasonal bloom-box concept, and the software powering the website, subscriber
        portal and grower portal are the intellectual property of Heritage Dahlias NZ Limited.
      </P>
      <P>
        These rights are protected by copyright, trade-mark and design legislation in Aotearoa
        New Zealand and under international treaty. Users are granted a limited, non-exclusive
        licence to view this content for personal, non-commercial use. Subscribers and growers
        must not reproduce, modify or commercially exploit Heritage intellectual property
        without prior written permission.
      </P>

      <H2 n="19">Insurance Coverage</H2>
      <P>
        Heritage Dahlias NZ Limited carries commercial general liability and product liability
        insurance covering the operation of the Bloom Club platform. Participating growers are
        independently required to carry their own insurance covering their growing, packing and
        delivery operations as set out in the Grower Terms.
      </P>
      <P>
        Personal information collected through Heritage Bloom Club systems is stored on insured,
        access-controlled cloud infrastructure provided by reputable third-party hosts.
      </P>

      <P className="font-display-italic text-center text-[var(--berry-deep)] mt-10">
        Local Flowers. Local Growers. Local Communities.
      </P>
    </LegalLayout>
  );
}
